/**
 * organizationStructureService.ts
 * Catálogo de departamentos/cargos (org_departments, workflow_roles_v9).
 * Vínculo membro ↔ depto ↔ cargo ↔ gestor: SSOT em user_organizations.
 */
import { supabase } from '../lib/supabaseClient';
import { updateHierarchy } from './organizationService';

// ============================================================================
// TYPES
// ============================================================================

export interface OrgDepartment {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  color: string;
  active: boolean;
  sortOrder: number;
}

export interface OrgRole {
  id: string;
  organizationId: string;
  roleKey: string;
  name: string;
  description: string | null;
  departmentId: string | null;
  active: boolean;
  isSystem: boolean;
}

/** Unified assignment: user ↔ department ↔ role */
export interface UserAssignment {
  id: string;
  userId: string;
  departmentId: string;
  roleId: string;
}

export interface OrgStructure {
  departments: OrgDepartment[];
  roles: OrgRole[];
}

// ============================================================================
// DEPARTMENTS
// ============================================================================

export async function getDepartments(orgId: string): Promise<OrgDepartment[]> {
  const { data, error } = await supabase
    .from('org_departments')
    .select('id, organization_id, name, description, color, active, sort_order')
    .eq('organization_id', orgId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapDepartmentRow);
}

export async function createDepartment(
  orgId: string,
  name: string,
  color?: string,
  description?: string,
): Promise<OrgDepartment> {
  const { data, error } = await supabase
    .from('org_departments')
    .insert({
      organization_id: orgId,
      name: name.trim(),
      color: color ?? '#64748B',
      description: description ?? null,
    })
    .select('id, organization_id, name, description, color, active, sort_order')
    .single();

  if (error) throw error;
  return mapDepartmentRow(data);
}

export async function updateDepartment(
  deptId: string,
  updates: { name?: string; color?: string; description?: string; active?: boolean; sortOrder?: number },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) payload.name = updates.name.trim();
  if (updates.color !== undefined) payload.color = updates.color;
  if (updates.description !== undefined) payload.description = updates.description;
  if (updates.active !== undefined) payload.active = updates.active;
  if (updates.sortOrder !== undefined) payload.sort_order = updates.sortOrder;

  const { error } = await supabase
    .from('org_departments')
    .update(payload)
    .eq('id', deptId);

  if (error) throw error;
}

export async function deleteDepartment(deptId: string): Promise<void> {
  const { error } = await supabase
    .from('org_departments')
    .delete()
    .eq('id', deptId);
  if (error) throw error;
}

// ============================================================================
// ROLES
// ============================================================================

export async function getRoles(orgId: string): Promise<OrgRole[]> {
  const { data, error } = await supabase
    .from('workflow_roles_v9')
    .select('id, organization_id, role_key, name, description, department_id, active, is_system')
    .eq('organization_id', orgId)
    .order('name', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRoleRow);
}

export async function getRolesByDepartment(orgId: string, departmentId: string): Promise<OrgRole[]> {
  const { data, error } = await supabase
    .from('workflow_roles_v9')
    .select('id, organization_id, role_key, name, description, department_id, active, is_system')
    .eq('organization_id', orgId)
    .eq('department_id', departmentId)
    .order('name', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRoleRow);
}

export async function createRole(
  orgId: string,
  name: string,
  departmentId?: string | null,
  description?: string,
): Promise<OrgRole> {
  const roleKey = name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  const { data, error } = await supabase
    .from('workflow_roles_v9')
    .insert({
      organization_id: orgId,
      role_key: roleKey,
      name: name.trim(),
      department_id: departmentId ?? null,
      description: description ?? null,
    })
    .select('id, organization_id, role_key, name, description, department_id, active, is_system')
    .single();

  if (error) throw error;
  return mapRoleRow(data);
}

export async function updateRole(
  roleId: string,
  updates: { name?: string; departmentId?: string | null; description?: string; active?: boolean },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) {
    payload.name = updates.name.trim();
    payload.role_key = updates.name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }
  if (updates.departmentId !== undefined) payload.department_id = updates.departmentId;
  if (updates.description !== undefined) payload.description = updates.description;
  if (updates.active !== undefined) payload.active = updates.active;

  const { error } = await supabase
    .from('workflow_roles_v9')
    .update(payload)
    .eq('id', roleId);

  if (error) throw error;
}

export async function deleteRole(roleId: string): Promise<void> {
  const { error } = await supabase
    .from('workflow_roles_v9')
    .delete()
    .eq('id', roleId);
  if (error) throw error;
}

// ============================================================================
// FULL STRUCTURE (departments + roles in one call)
// ============================================================================

export async function getOrgStructure(orgId: string): Promise<OrgStructure> {
  const [departments, roles] = await Promise.all([
    getDepartments(orgId),
    getRoles(orgId),
  ]);
  return { departments, roles };
}

// ============================================================================
// USER ASSIGNMENTS (unified: user ↔ department ↔ role)
// ============================================================================

/** Vínculo primário do membro (lê user_organizations). */
export async function getUserAssignments(orgId: string, userId: string): Promise<UserAssignment[]> {
  const { data, error } = await supabase
    .from('user_organizations')
    .select('user_id, department_id, role_id')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data?.department_id || !data?.role_id) return [];
  return [{
    id: `membership-${userId}`,
    userId: data.user_id,
    departmentId: data.department_id,
    roleId: data.role_id,
  }];
}

/** Get department IDs the user belongs to (derived from assignments) */
export async function getUserDepartments(orgId: string, userId: string): Promise<string[]> {
  const assignments = await getUserAssignments(orgId, userId);
  return [...new Set(assignments.map(a => a.departmentId))];
}

/** Get role IDs the user has (derived from assignments) */
export async function getUserRoleIds(orgId: string, userId: string): Promise<string[]> {
  const assignments = await getUserAssignments(orgId, userId);
  return [...new Set(assignments.map(a => a.roleId))];
}

/** Define departamento + cargo do membro (SSOT: user_organizations). */
export async function addUserAssignment(
  orgId: string,
  userId: string,
  departmentId: string,
  roleId: string,
): Promise<void> {
  if (!orgId || !userId) throw new Error('addUserAssignment: orgId e userId são obrigatórios.');
  if (!departmentId || !roleId) {
    throw new Error('Selecione um departamento e um cargo antes de salvar o vínculo.');
  }
  await updateHierarchy(orgId, userId, { department_id: departmentId, role_id: roleId });
}

/** Remove a specific (department, role) assignment */
export async function removeUserAssignment(assignmentId: string): Promise<void> {
  // Read before delete for legacy sync
  const { data: row } = await supabase
    .from('user_assignments')
    .select('user_id, department_id, role_id')
    .eq('id', assignmentId)
    .single();

  const { error } = await supabase
    .from('user_assignments')
    .delete()
    .eq('id', assignmentId);
  if (error) throw error;

  // Clean up legacy tables if no more assignments reference them
  if (row) {
    const { data: remainingDept } = await supabase
      .from('user_assignments')
      .select('id')
      .eq('user_id', row.user_id)
      .eq('department_id', row.department_id)
      .limit(1);
    if (!remainingDept?.length) {
      await supabase.from('user_departments')
        .delete()
        .eq('user_id', row.user_id)
        .eq('department_id', row.department_id);
    }

    const { data: remainingRole } = await supabase
      .from('user_assignments')
      .select('id')
      .eq('user_id', row.user_id)
      .eq('role_id', row.role_id)
      .limit(1);
    if (!remainingRole?.length) {
      await supabase.from('user_roles')
        .delete()
        .eq('user_id', row.user_id)
        .eq('role_id', row.role_id);
    }
  }
}

// Legacy aliases — kept for consumers that haven't migrated yet
export async function assignUserToDepartment(orgId: string, userId: string, departmentId: string): Promise<void> {
  console.warn('[DEPRECATED] assignUserToDepartment — use addUserAssignment instead');
  await supabase.from('user_departments').upsert(
    { organization_id: orgId, user_id: userId, department_id: departmentId },
    { onConflict: 'user_id,department_id' },
  );
}

export async function removeUserFromDepartment(userId: string, departmentId: string): Promise<void> {
  console.warn('[DEPRECATED] removeUserFromDepartment — use removeUserAssignment instead');
  await supabase.from('user_departments').delete().eq('user_id', userId).eq('department_id', departmentId);
}

export async function assignUserToRole(orgId: string, userId: string, roleId: string): Promise<void> {
  console.warn('[DEPRECATED] assignUserToRole — use addUserAssignment instead');
  await supabase.from('user_roles').upsert(
    { organization_id: orgId, user_id: userId, role_id: roleId },
    { onConflict: 'user_id,role_id' },
  );
}

export async function removeUserFromRole(userId: string, roleId: string): Promise<void> {
  console.warn('[DEPRECATED] removeUserFromRole — use removeUserAssignment instead');
  await supabase.from('user_roles').delete().eq('user_id', userId).eq('role_id', roleId);
}

// ============================================================================
// VALIDATION
// ============================================================================

export interface StructureValidation {
  rolesWithoutUsers: OrgRole[];
  departmentsWithoutUsers: OrgDepartment[];
  rolesWithoutDepartment: OrgRole[];
}

export async function validateStructure(orgId: string): Promise<StructureValidation> {
  const [structure, memberships] = await Promise.all([
    getOrgStructure(orgId),
    supabase.from('user_organizations').select('role_id, department_id').eq('organization_id', orgId),
  ]);

  const rolesWithUsers = new Set((memberships.data || []).map(r => r.role_id).filter(Boolean));
  const deptsWithUsers = new Set((memberships.data || []).map(r => r.department_id).filter(Boolean));

  return {
    rolesWithoutUsers: structure.roles.filter(r => !rolesWithUsers.has(r.id)),
    departmentsWithoutUsers: structure.departments.filter(d => !deptsWithUsers.has(d.id)),
    rolesWithoutDepartment: structure.roles.filter(r => !r.departmentId),
  };
}

// ============================================================================
// ROW MAPPERS
// ============================================================================

function mapDepartmentRow(row: Record<string, any>): OrgDepartment {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    description: row.description ?? null,
    color: row.color ?? '#64748B',
    active: row.active ?? true,
    sortOrder: row.sort_order ?? 0,
  };
}

function mapRoleRow(row: Record<string, any>): OrgRole {
  return {
    id: row.id,
    organizationId: row.organization_id,
    roleKey: row.role_key,
    name: row.name,
    description: row.description ?? null,
    departmentId: row.department_id ?? null,
    active: row.active ?? true,
    isSystem: row.is_system ?? false,
  };
}

// ============================================================================
// REPORTS-TO (direct manager per member, tenant-scoped)
// ============================================================================

/**
 * Updates the direct manager (reports_to_user_id) of a member within an org.
 * Pass `reportsToUserId = null` to clear the link.
 */
export async function updateMemberReportsTo(
  orgId: string,
  userId: string,
  reportsToUserId: string | null,
): Promise<void> {
  if (reportsToUserId && reportsToUserId === userId) {
    throw new Error('Um usuário não pode reportar a si mesmo.');
  }

  const { error } = await supabase
    .from('user_organizations')
    .update({ reports_to_user_id: reportsToUserId })
    .eq('organization_id', orgId)
    .eq('user_id', userId);

  if (error) throw error;
}

// ============================================================================
// ORG-WIDE ASSIGNMENT QUERIES (for the visual organogram)
// ============================================================================

/** @deprecated Preferir campos em user_organizations via useOrgMembers. */
export async function getAllAssignments(orgId: string): Promise<UserAssignment[]> {
  const { data, error } = await supabase
    .from('user_organizations')
    .select('user_id, department_id, role_id')
    .eq('organization_id', orgId);

  if (error) return [];
  return (data || [])
    .filter(r => r.department_id && r.role_id)
    .map(r => ({
      id: `membership-${r.user_id}`,
      userId: r.user_id,
      departmentId: r.department_id!,
      roleId: r.role_id!,
    }));
}

/** Atualiza departamento + cargo primários do membro (SSOT: user_organizations). */
export async function setMemberPrimaryAssignment(
  orgId: string,
  userId: string,
  departmentId: string,
  roleId: string,
): Promise<void> {
  if (!departmentId || !roleId) {
    throw new Error('Selecione um departamento e um cargo antes de salvar o vínculo.');
  }
  await updateHierarchy(orgId, userId, { department_id: departmentId, role_id: roleId });
}

